/**
 * Bulk Student Upload Component
 * Reusable component for Admin and Agent dashboards
 */
import { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  Upload, FileSpreadsheet, Download, CheckCircle, XCircle, 
  AlertTriangle, Loader2, Users, Info
} from 'lucide-react';

// CSV Template headers
const CSV_HEADERS = [
  'full_name',
  'email', 
  'phone',
  'education_level',
  'preferred_countries',
  'field_of_study',
  'budget_range',
  'notes'
];

// Sample data for template
const SAMPLE_DATA = [
  'John Smith,john@example.com,+44123456789,Bachelors,UK,Computer Science,20000-30000,Interested in January intake',
  'Jane Doe,jane@example.com,+44987654321,Masters,"UK, Canada",Business,30000-50000,Scholarship applicant'
];

export const BulkStudentUpload = ({ onUpload, isAgent = false }) => {
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [results, setResults] = useState(null);
  const fileInputRef = useRef(null);

  const downloadTemplate = () => {
    const csvContent = [
      CSV_HEADERS.join(','),
      ...SAMPLE_DATA
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'student_upload_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Template downloaded');
  };

  const parseCSV = (text) => {
    const lines = text.split('\n').filter(line => line.trim());
    if (lines.length < 2) {
      throw new Error('CSV must have headers and at least one data row');
    }

    const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
    
    // Validate required headers
    const requiredHeaders = ['full_name', 'email'];
    for (const req of requiredHeaders) {
      if (!headers.includes(req)) {
        throw new Error(`Missing required column: ${req}`);
      }
    }

    const students = [];
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i];
      if (!line.trim()) continue;

      // Handle quoted fields with commas
      const values = [];
      let current = '';
      let inQuotes = false;
      
      for (const char of line) {
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
          values.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      values.push(current.trim());

      const student = {};
      headers.forEach((header, idx) => {
        student[header] = values[idx] || '';
      });

      // Validate required fields
      if (!student.full_name || !student.email) {
        throw new Error(`Row ${i + 1}: Missing required field (full_name or email)`);
      }

      // Validate email format
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(student.email)) {
        throw new Error(`Row ${i + 1}: Invalid email format - ${student.email}`);
      }

      students.push(student);
    }

    return students;
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const students = parseCSV(event.target.result);
        setPreview(students);
        setResults(null);
      } catch (error) {
        toast.error(error.message);
        setPreview(null);
      }
    };
    reader.readAsText(file);
  };

  const handleUpload = async () => {
    if (!preview || preview.length === 0) {
      toast.error('No students to upload');
      return;
    }

    setUploading(true);
    try {
      const response = await onUpload(preview);
      setResults(response.data);
      
      if (response.data.success > 0) {
        toast.success(`Successfully uploaded ${response.data.success} students`);
      }
      if (response.data.failed > 0) {
        toast.warning(`${response.data.failed} students failed to upload`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const resetForm = () => {
    setPreview(null);
    setResults(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClose = () => {
    setOpen(false);
    resetForm();
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button 
          variant="outline" 
          className="border-teal-300 text-teal-700 hover:bg-teal-50 dark:border-teal-700 dark:text-teal-300 dark:hover:bg-teal-900/20"
          data-testid="bulk-upload-btn"
        >
          <Upload className="w-4 h-4 mr-2" />
          Bulk Upload
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-white dark:bg-slate-800 max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-gray-800 dark:text-white flex items-center gap-2">
            <Users className="w-5 h-5 text-teal-600" />
            Bulk Student Upload
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {/* Instructions */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <div className="flex items-start gap-2">
              <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800 dark:text-blue-200">
                <p className="font-medium mb-2">Instructions:</p>
                <ol className="list-decimal list-inside space-y-1">
                  <li>Download the CSV template below</li>
                  <li>Fill in student details (full_name and email are required)</li>
                  <li>For multiple countries, use quotes: &quot;UK, Canada, Germany&quot;</li>
                  <li>Upload the completed CSV file</li>
                </ol>
              </div>
            </div>
          </div>

          {/* Download Template */}
          <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-slate-700 rounded-lg">
            <div className="flex items-center gap-3">
              <FileSpreadsheet className="w-8 h-8 text-green-600" />
              <div>
                <p className="font-medium text-gray-800 dark:text-white">CSV Template</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">Download and fill with student data</p>
              </div>
            </div>
            <Button 
              onClick={downloadTemplate}
              variant="outline"
              className="border-green-300 text-green-700 hover:bg-green-50"
              data-testid="download-template-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Download
            </Button>
          </div>

          {/* File Upload */}
          <div className="space-y-2">
            <Label>Upload CSV File</Label>
            <Input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileSelect}
              className="cursor-pointer"
              data-testid="csv-file-input"
            />
          </div>

          {/* Preview */}
          {preview && preview.length > 0 && !results && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="font-medium text-gray-800 dark:text-white">
                  Preview ({preview.length} students)
                </h4>
                <Badge className="bg-teal-100 text-teal-700">{preview.length} ready</Badge>
              </div>
              <div className="max-h-48 overflow-y-auto border border-gray-200 dark:border-slate-600 rounded-lg">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-slate-700 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left text-gray-600 dark:text-gray-300">#</th>
                      <th className="px-3 py-2 text-left text-gray-600 dark:text-gray-300">Name</th>
                      <th className="px-3 py-2 text-left text-gray-600 dark:text-gray-300">Email</th>
                      <th className="px-3 py-2 text-left text-gray-600 dark:text-gray-300">Countries</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.slice(0, 10).map((s, idx) => (
                      <tr key={idx} className="border-t border-gray-100 dark:border-slate-700">
                        <td className="px-3 py-2 text-gray-500">{idx + 1}</td>
                        <td className="px-3 py-2 text-gray-800 dark:text-white">{s.full_name}</td>
                        <td className="px-3 py-2 text-gray-600 dark:text-gray-300">{s.email}</td>
                        <td className="px-3 py-2 text-gray-600 dark:text-gray-300">{s.preferred_countries || '-'}</td>
                      </tr>
                    ))}
                    {preview.length > 10 && (
                      <tr className="border-t border-gray-100 dark:border-slate-700">
                        <td colSpan={4} className="px-3 py-2 text-center text-gray-500">
                          ... and {preview.length - 10} more
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div className="flex gap-3">
                <Button
                  onClick={handleUpload}
                  disabled={uploading}
                  className="flex-1 bg-teal-600 hover:bg-teal-700 text-white"
                  data-testid="upload-students-btn"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Upload {preview.length} Students
                    </>
                  )}
                </Button>
                <Button
                  onClick={resetForm}
                  variant="outline"
                  disabled={uploading}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {/* Results */}
          {results && (
            <div className="space-y-4">
              <h4 className="font-medium text-gray-800 dark:text-white">Upload Results</h4>
              
              {/* Summary */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-gray-50 dark:bg-slate-700 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-gray-800 dark:text-white">{results.total}</p>
                  <p className="text-sm text-gray-500">Total</p>
                </div>
                <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-green-600">{results.success}</p>
                  <p className="text-sm text-green-600">Success</p>
                </div>
                <div className={`rounded-lg p-3 text-center ${results.failed > 0 ? 'bg-red-50 dark:bg-red-900/20' : 'bg-gray-50 dark:bg-slate-700'}`}>
                  <p className={`text-2xl font-bold ${results.failed > 0 ? 'text-red-600' : 'text-gray-400'}`}>{results.failed}</p>
                  <p className={`text-sm ${results.failed > 0 ? 'text-red-600' : 'text-gray-500'}`}>Failed</p>
                </div>
              </div>

              {/* Success List */}
              {results.created_students?.length > 0 && (
                <div className="border border-green-200 dark:border-green-800 rounded-lg p-3">
                  <h5 className="font-medium text-green-700 dark:text-green-300 mb-2 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    Successfully Added ({results.created_students.length})
                  </h5>
                  <div className="max-h-32 overflow-y-auto text-sm">
                    {results.created_students.slice(0, 5).map((s, idx) => (
                      <p key={idx} className="text-gray-600 dark:text-gray-300">
                        {s.full_name} - {s.email}
                        {!isAgent && s.temp_password && (
                          <span className="text-gray-400 ml-2">(Pass: {s.temp_password})</span>
                        )}
                      </p>
                    ))}
                    {results.created_students.length > 5 && (
                      <p className="text-gray-500">... and {results.created_students.length - 5} more</p>
                    )}
                  </div>
                </div>
              )}

              {/* Error List */}
              {results.errors?.length > 0 && (
                <div className="border border-red-200 dark:border-red-800 rounded-lg p-3">
                  <h5 className="font-medium text-red-700 dark:text-red-300 mb-2 flex items-center gap-2">
                    <XCircle className="w-4 h-4" />
                    Failed ({results.errors.length})
                  </h5>
                  <div className="max-h-32 overflow-y-auto text-sm space-y-1">
                    {results.errors.map((err, idx) => (
                      <p key={idx} className="text-red-600 dark:text-red-400">
                        Row {err.row}: {err.email} - {err.error}
                      </p>
                    ))}
                  </div>
                </div>
              )}

              {/* Close Button */}
              <Button
                onClick={handleClose}
                className="w-full bg-teal-600 hover:bg-teal-700 text-white"
              >
                Done
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BulkStudentUpload;
